import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '../components/ui/alert-dialog';
import { getSessions, revokeSession, revokeAllOtherSessions } from '../lib/api';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Monitor,
  Smartphone,
  Tablet,
  Globe,
  Clock,
  MapPin,
  Shield,
  LogOut,
  Loader2,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';

const getDeviceIcon = (deviceType) => {
  switch (deviceType?.toLowerCase()) {
    case 'mobile':
      return Smartphone;
    case 'tablet':
      return Tablet;
    default:
      return Monitor;
  }
};

const formatTimeAgo = (dateString) => {
  if (!dateString) return 'Unknown';
  
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  
  return date.toLocaleDateString();
};

const Sessions = () => {
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState('');
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(null);
  const [revokingAll, setRevokingAll] = useState(false);
  const { logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      const res = await getSessions();
      setSessions(res.data.sessions);
      setCurrentSessionId(res.data.current_session_id);
    } catch (error) {
      toast.error('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeSession = async (sessionId) => {
    setRevoking(sessionId);
    try {
      const res = await revokeSession(sessionId);
      
      if (res.data.logged_out) {
        toast.success('Session revoked. Logging out...');
        setTimeout(() => {
          logout();
          navigate('/');
        }, 1000);
      } else {
        toast.success('Session revoked successfully');
        setSessions(sessions.filter(s => s.id !== sessionId));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke session');
    } finally {
      setRevoking(null);
    }
  };

  const handleRevokeAllOther = async () => {
    setRevokingAll(true);
    try {
      const res = await revokeAllOtherSessions();
      toast.success(res.data.message);
      // Reload to get updated list
      await loadSessions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke sessions');
    } finally {
      setRevokingAll(false);
    }
  };

  const otherSessionsCount = sessions.filter(s => s.id !== currentSessionId).length;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <motion.div 
        className="space-y-8 max-w-4xl"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="sessions-page"
      >
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-heading font-bold flex items-center gap-3">
              <Shield className="w-8 h-8 text-emerald-600" />
              Active Sessions
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage devices that are logged in to your account
            </p>
          </div>
          
          {otherSessionsCount > 0 && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button 
                  variant="outline" 
                  className="rounded-xl border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                  disabled={revokingAll}
                  data-testid="revoke-all-btn"
                >
                  {revokingAll ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <LogOut className="w-4 h-4 mr-2" />
                  )}
                  Sign Out Other Devices
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Sign out of other devices?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will revoke {otherSessionsCount} other session{otherSessionsCount > 1 ? 's' : ''}. 
                    You'll remain logged in on this device.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={handleRevokeAllOther}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Sign Out All
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>

        {/* Security Notice */}
        <Card className="border-emerald-200 bg-emerald-50/50 dark:bg-emerald-950/20 dark:border-emerald-900">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-emerald-600 mt-0.5" />
              <div>
                <p className="font-medium text-emerald-800 dark:text-emerald-300">
                  Security Tip
                </p>
                <p className="text-sm text-emerald-700 dark:text-emerald-400">
                  If you see any device you don't recognize, revoke it immediately and change your password.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sessions List */}
        <div className="space-y-4">
          <AnimatePresence>
            {sessions.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Monitor className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No active sessions found</p>
                </CardContent>
              </Card>
            ) : (
              sessions.map((session, index) => {
                const DeviceIcon = getDeviceIcon(session.device_type);
                const isCurrent = session.id === currentSessionId;
                
                return (
                  <motion.div
                    key={session.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card 
                      className={`overflow-hidden transition-all ${
                        isCurrent 
                          ? 'border-emerald-300 dark:border-emerald-700 ring-1 ring-emerald-200 dark:ring-emerald-800' 
                          : 'hover:border-border/80'
                      }`}
                      data-testid={`session-card-${session.id}`}
                    >
                      <CardContent className="p-6">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          {/* Device Info */}
                          <div className="flex items-start gap-4">
                            <div className={`p-3 rounded-xl ${
                              isCurrent 
                                ? 'bg-emerald-500/10' 
                                : 'bg-muted'
                            }`}>
                              <DeviceIcon className={`w-6 h-6 ${
                                isCurrent ? 'text-emerald-600' : 'text-muted-foreground'
                              }`} />
                            </div>
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <h3 className="font-semibold">
                                  {session.device_name || 'Unknown Device'}
                                </h3>
                                {isCurrent && (
                                  <Badge 
                                    variant="secondary" 
                                    className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-0"
                                  >
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    This Device
                                  </Badge>
                                )}
                              </div>
                              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <Globe className="w-3.5 h-3.5" />
                                  {session.browser}
                                </span>
                                <span>{session.os}</span>
                              </div>
                              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-3 h-3" />
                                  {session.ip_address}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  Active {formatTimeAgo(session.last_active)}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-2 md:ml-4">
                            {isCurrent ? (
                              <span className="text-sm text-muted-foreground px-3 py-2">
                                Current session
                              </span>
                            ) : (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
                                    disabled={revoking === session.id}
                                    data-testid={`revoke-btn-${session.id}`}
                                  >
                                    {revoking === session.id ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <>
                                        <LogOut className="w-4 h-4 mr-1" />
                                        Sign Out
                                      </>
                                    )}
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle className="flex items-center gap-2">
                                      <AlertTriangle className="w-5 h-5 text-orange-500" />
                                      Revoke this session?
                                    </AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This will sign out the {session.device_name || 'device'} ({session.browser} on {session.os}). 
                                      The user will need to log in again.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction 
                                      onClick={() => handleRevokeSession(session.id)}
                                      className="bg-red-600 hover:bg-red-700"
                                    >
                                      Revoke Session
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })
            )}
          </AnimatePresence>
        </div>

        {/* Session Info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-heading">About Sessions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <p>
              Sessions are created each time you log in to ParachPay from a new device or browser.
            </p>
            <p>
              Sessions automatically expire after 7 days of inactivity. You can manually revoke 
              any session at any time for security.
            </p>
            <p>
              If you suspect unauthorized access, revoke all sessions and change your password immediately.
            </p>
          </CardContent>
        </Card>
      </motion.div>
    </Layout>
  );
};

export default Sessions;
